import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DateFormatter } from '../utils/DateFormatter';
import classes from '../OrganizationUsagePage.module.css';

// Extend Window interface to include PDF export flag
declare global {
  interface Window {
    __ORGANIZATION_USAGE_PDF_READY__?: boolean;
  }
}

export interface OrganizationUsagePdfWrapperProps {
  children: React.ReactNode;
  periodStart: Date | null;
  periodEnd: Date | null;
  isReady: boolean;
}

/**
 * PDF Wrapper for Organization Usage Page
 * Adds cover page and signals readiness for PDF export
 */
export const OrganizationUsagePdfWrapper: React.FC<OrganizationUsagePdfWrapperProps> = ({
  children,
  periodStart,
  periodEnd,
  isReady,
}) => {
  const { t, i18n } = useTranslation();

  // Signal to PDF export when content is ready
  useEffect(() => {
    if (isReady) {
      const timer = setTimeout(() => {
        window.__ORGANIZATION_USAGE_PDF_READY__ = true;
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isReady]);

  const formatPeriod = () => {
    if (!periodStart || !periodEnd) {
      return t('usage.overview.pdf.allTime');
    }

    return `${DateFormatter.formatLong(periodStart, i18n.language)} - ${DateFormatter.formatLong(periodEnd, i18n.language)}`;
  };

  const today = DateFormatter.formatLong(new Date(), i18n.language);

  return (
    <>
      {/* PDF Cover Page - only visible in print/PDF */}
      <div className={classes.pdfCoverPage} data-print="only">
        <div className={classes.pdfCoverContent}>
          <div className={classes.pdfCoverLogo}>Trustpulse</div>
          <h1 className={classes.pdfCoverTitle}>
            {t('usage.overview.pdf.coverTitle')}
          </h1>
          <div className={classes.pdfCoverSubtitle}>
            {t('usage.overview.pdf.coverSubtitle')}
          </div>
          <div className={classes.pdfCoverDescription}>
            {t('usage.overview.pdf.coverDescription')}
          </div>
          <div className={classes.pdfCoverMetadata}>
            <div className={classes.pdfCoverMetaItem}>
              <div className={classes.pdfCoverMetaLabel}>
                {t('usage.overview.pdf.period')}
              </div>
              <div className={classes.pdfCoverMetaValue}>
                {formatPeriod()}
              </div>
            </div>
            <div className={classes.pdfCoverMetaItem}>
              <div className={classes.pdfCoverMetaLabel}>
                {t('usage.overview.pdf.generatedDate')}
              </div>
              <div className={classes.pdfCoverMetaValue}>
                {today}
              </div>
            </div>
          </div>
        </div>
        <div className={classes.pdfCoverFooter}>
          {t('usage.overview.pdf.confidential')}
        </div>
      </div>

      {/* Actual page content */}
      {children}
    </>
  );
};
